import type { KeyboardEventHandler } from 'react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import type { FormatOptionLabelMeta } from 'react-select';
import SelectAsync from 'react-select';
import { searchKols } from '@/api/search';
import { buildSelectStyles, buildSelectTheme } from '@/components/Chat.Filters/Presentation';
import { SearchQueryHint } from '@/components/presentation';
import { SelectLocation } from '@/components/Search/Location';
import { useTheme } from '@/components/Theme';
import { KolSearchStatus } from '@/enums/kol';
import { useHasSmartRouting } from '@/store/selectors';
import type { KolSearchForm, KolSearchResult } from '@/types/kol';
import { useBrowserLocation } from '@/utils/hooks/useBrowserLocation';
import { BrowserLocationError } from './BrowserLocationError';
import {
  DispatchKolSearchFormContext,
  KolSearchStatusContext,
  KolSearchSubmitContext,
  KolSearchFormContext,
} from './context';
import { BasicSystemUserMessage } from './Message.System.Basic';

export const KolSearchFormMessage = () => {
  const searchStatus = useContext(KolSearchStatusContext);
  const handleSubmit = useContext(KolSearchSubmitContext);
  const formState = useContext(KolSearchFormContext);
  const setFormState = useContext(DispatchKolSearchFormContext);
  const inputFocused = useRef(false);

  const copy = `Let's get started. First lets find the HCP you'd like to profile!`;
  const instructions = `Enter details to find the HCP you would like to profile.`;

  const submitDisabled = useMemo(() => {
    return (!formState.name && !formState.location) || searchStatus !== KolSearchStatus.FormEntry;
  }, [formState.location, formState.name, searchStatus]);

  const inputDisabled = useMemo(() => {
    return searchStatus !== KolSearchStatus.FormEntry;
  }, [searchStatus]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(e => {

    if (!inputFocused.current) return;

    if (submitDisabled) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }, [submitDisabled, handleSubmit]);

  const handleInputBlur = useCallback(() => {
    inputFocused.current = false;
  }, []);

  const handleInputFocus = useCallback(() => {
    inputFocused.current = true;
  }, []);

  const onKolSelect = useCallback((kol: KolSearchResult) => {
    setFormState({ kolId: kol.id, name: kol.name });
    handleSubmit();
  }, [handleSubmit, setFormState]);

  return (
    <MessageContainer>
      <BasicSystemUserMessage>
        <Body>
          {copy}
          <Divider />
          <Instructions>{instructions}</Instructions>

          <KolSearchInputForm
            formState={formState}
            inputDisabled={inputDisabled}
            onKeyDown={handleKeyDown}
            onInputFocus={handleInputFocus}
            onInputBlur={handleInputBlur}
            onKolSelect={onKolSelect}
            setFormState={setFormState} />
          <Divider />
          <SearchQueryHint
            disabled={submitDisabled}
            onClick={handleSubmit}
            value="Search for profiles." />
        </Body>
      </BasicSystemUserMessage>
    </MessageContainer>
  );
};

type HcpSelectProps = {
  disabled?: boolean;
  formatOptionLabel?: (data: KolSearchResult, formatOptionLabelMeta: FormatOptionLabelMeta<KolSearchResult>) => React.ReactNode
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onInputChange: (e: string) => void;
  onSelection: (kol: KolSearchResult) => void;
  placeholder?: string;
  inputValue: string;
  isClearable?: boolean;
  value?: KolSearchResult | null;
};

export function SelectHcp({
  disabled,
  formatOptionLabel,
  onKeyDown,
  onInputFocus,
  onInputBlur,
  onInputChange,
  onSelection,
  placeholder = 'Search Hcps',
  inputValue,
  isClearable,
  value,
}: HcpSelectProps) {
  const searchQuery = useQuery({
    queryKey: ['get:kol-search', inputValue],
    queryFn: () => searchKols({ name: inputValue }),
    enabled: inputValue?.length > 2 && !disabled,
    placeholderData: { results: [] },
  });

  const theme = useTheme();

  const selectStyles = useMemo(() => buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => buildSelectTheme(theme), [theme]);

  return (
    <SelectAsync<KolSearchResult>
      menuPortalTarget={document.body}
      isDisabled={disabled}
      isClearable={isClearable}
      onKeyDown={searchQuery.data.results.length ? undefined : onKeyDown}
      onFocus={onInputFocus}
      onBlur={onInputBlur}
      onChange={onSelection}
      inputValue={inputValue}
      value={value}
      isLoading={searchQuery.isFetching}
      options={searchQuery.data.results}
      getOptionLabel={option => option.name}
      getOptionValue={o => o.id.toString()}
      formatOptionLabel={formatOptionLabel || formatKolOptionLabel}
      noOptionsMessage={() => null}
      onInputChange={(e, meta) => meta.action === 'input-change' ? onInputChange(e) : null}
      placeholder={placeholder}
      styles={selectStyles}
      theme={selectTheme} />
  );
}

type FormProps = {
  className?: string;
  inputDisabled: boolean;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onKolSelect: (kol: KolSearchResult) => void;
  formState: Partial<KolSearchForm>;
  setFormState: (form: Partial<KolSearchForm>) => void;
};

export const KolSearchInputForm = ({
  className,
  formState,
  inputDisabled,
  onInputBlur,
  onInputFocus,
  onKeyDown,
  onKolSelect,
  setFormState,
}: FormProps) => {

  const searchQuery = useQuery({
    queryKey: ['get:kol-search', formState.name],
    queryFn: () => searchKols({ name: formState.name }),
    enabled: formState.name?.length > 2,
    placeholderData: { results: [] },
  });

  const [locationError, setLocationError] = useState<string | null>(null);
  const { getLocation, isSupported, state } = useBrowserLocation({
    onSuccess: result => {
      setFormState({ location: { id: 'browser-location', latitude: result.latitude, longitude: result.longitude, name: null } });
    }, onError: setLocationError,
  });

  const hasSmartRouting = useHasSmartRouting();

  return (
    <div className={className}>
      <FormItem>
        <Label>Search by Name</Label>
        <SelectHcp
          placeholder="Name"
          disabled={inputDisabled}
          onKeyDown={searchQuery.data.results.length ? undefined : onKeyDown}
          onInputFocus={onInputFocus}
          onInputBlur={onInputBlur}
          onInputChange={name => setFormState({ ...formState, name })}
          onSelection={kol => onKolSelect(kol)}
          inputValue={formState.name} />
      </FormItem>
      {hasSmartRouting && (
        <>
          <Label>OR</Label>
          <FormItem>
            <Label>Search by address or current location</Label>
            <SelectLocation
              styles={{
                container: (baseStyles) => ({ ...baseStyles, width: '100%' }),
              }}
              isDisabled={inputDisabled}
              value={formState.location}
              onChange={location => setFormState({ ...formState, location })} />
            {isSupported &&
              <GetLocationSection>
                <GetLocationButton onClick={getLocation}>Share my current location</GetLocationButton>
                {state === 'error' && <BrowserLocationError errorMessage={locationError} />}
              </GetLocationSection>}
          </FormItem>
        </>
      )}
    </div >
  );
};

function formatKolOptionLabel(result: KolSearchResult) {
  const specialty = result.specialties?.[0];
  const primaryAffiliation = result.affiliations?.[0]?.name;
  return (
    <div>
      {result.name}{specialty ? <KolOptionSubText>({specialty})</KolOptionSubText> : null}
      {primaryAffiliation ? <KolResultSecondaryText>{primaryAffiliation}</KolResultSecondaryText> : null}
    </div>
  );
}

const KolResultSecondaryText = styled.div(({ theme }) => ({
  color: theme.palette.gray.main,
  marginTop: 5,
  fontSize: 15,
}));
const KolOptionSubText = styled.span(({ theme }) => ({
  fontSize: 14,
  marginLeft: 5,
  color: theme.palette.gray.main,
}));


const Body = styled.div({
  width: 500,
});

const MessageContainer = styled.div({
  paddingRight: 20,
  paddingLeft: 20,
});

const Divider = styled.div(({ theme }) => ({
  width: '100%',
  height: 2,
  backgroundColor: theme.palette.gray.light2,
  margin: '15px 0',
}));

const Instructions = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  marginBottom: 20,
}));

const Label = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  marginBottom: 10,
}));

const FormItem = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  marginBottom: 10,
});

const GetLocationSection = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  marginTop: 5,
});

const GetLocationButton = styled.div(({ theme }) => ({
  cursor: 'pointer',
  color: theme.palette.sentiment.primary.main,
  textDecoration: 'underline',
  fontSize: 14,
}));